import { useEffect, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import SelectTikTokAccountWithConnect from "../../../../TikTok/SelectTikTokAccountWithConnect";
import Select from "../../../../Form/Select";
import Switch from "../../../../Form/Switch";
import { optionsTikTokPrivacyLevel } from "../../../../../utils/options";
import { getExtensionFile } from "../../../../../utils/string";
import {
  FIELD_REQUIRED,
  TIKTOK_ACCOUNT_LABEL,
  TIKTOK_AUTO_ADD_MUSIC_LABEL,
  TIKTOK_DISABLE_COMMENT_LABEL,
  TIKTOK_DISABLE_DUET_LABEL,
  TIKTOK_DISABLE_STITCH_LABEL,
  TIKTOK_IS_AIGC_LABEL,
  TIKTOK_PRIVACY_LEVEL_LABEL,
  TYPE_CONTENT_TIKTOK_LABEL,
} from "../../../../../i18n/keysTranslations";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  TIKTOK_IMAGE_CONTENT_TYPE_OPTIONS,
  TIKTOK_VIDEO_CONTENT_TYPE_OPTIONS,
  TIKTOK_CONTENT_TYPE_CAROUSEL,
} from "../../../../../utils/constants";

const TikTokPlatformFields = ({
  control,
  errors,
  currentItem,
  t,
  isCarouselMode = false,
  currentItems = [],
  onContentTypeChange,
}) => {
  const { setValue, watch } = useFormContext();
  const format = getExtensionFile(currentItem?.url);
  const isImage = IMAGE_FORMATS.includes(format);
  const isVideo = VIDEO_FORMATS.includes(format);
  const currentContentType = watch("platformsData.tiktok.contentType");

  const isMultiImageCarousel = isCarouselMode && currentItems.length >= 2;
  const restrictToCarouselOnly = isMultiImageCarousel;

  const contentTypeOptions = useMemo(() => {
    if (!currentItem?.url) return [];

    let options = [];
    if (isImage) {
      const mapped = TIKTOK_IMAGE_CONTENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label),
      }));
      options = restrictToCarouselOnly
        ? mapped.filter(
            (option) => option.value === TIKTOK_CONTENT_TYPE_CAROUSEL
          )
        : mapped;
    }

    if (isVideo) {
      options = TIKTOK_VIDEO_CONTENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label),
      }));
    }

    return options;
  }, [currentItem?.url, isImage, isVideo, restrictToCarouselOnly, t]);

  useEffect(() => {
    if (contentTypeOptions.length === 0) return;
    const isStillValid = contentTypeOptions.some(
      (opt) => opt.value === currentContentType
    );
    if (currentContentType && !isStillValid) {
      setValue("platformsData.tiktok.contentType", contentTypeOptions[0].value);
    }
  }, [contentTypeOptions, currentContentType, setValue]);

  useEffect(() => {
    if (!currentItem?.url) return;

    if (isCarouselMode && currentItems.length >= 2 && !currentContentType) {
      setValue(
        "platformsData.tiktok.contentType",
        TIKTOK_CONTENT_TYPE_CAROUSEL
      );
    }
  }, [
    isCarouselMode,
    currentItems.length,
    setValue,
    currentItem?.url,
    currentContentType,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SelectTikTokAccountWithConnect
        variant="filled"
        name="platformsData.tiktok.account"
        label={t(TIKTOK_ACCOUNT_LABEL)}
        fullWidth
        control={control}
        errors={errors}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }}
      />

      <Select
        variant="filled"
        name="platformsData.tiktok.privacy_level"
        label={t(TIKTOK_PRIVACY_LEVEL_LABEL)}
        options={optionsTikTokPrivacyLevel}
        fullWidth
        control={control}
        errors={errors}
        doTranslate
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }}
      />

      <Select
        variant="filled"
        name="platformsData.tiktok.contentType"
        label={t(TYPE_CONTENT_TIKTOK_LABEL)}
        options={contentTypeOptions}
        fullWidth
        control={control}
        errors={errors}
        disabled={!currentItem?.url}
        validationChange={(value) => {
          if (onContentTypeChange) {
            return onContentTypeChange("tiktok", value);
          }
          return true;
        }}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="platformsData.tiktok.disable_comment"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                isActive={field.value}
                onChangeIsActive={(value) => field.onChange(value)}
                labelActive={TIKTOK_DISABLE_COMMENT_LABEL}
                labelDeactivate={TIKTOK_DISABLE_COMMENT_LABEL}
              />
            )}
          />
        </Grid>
        {isImage && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="platformsData.tiktok.auto_add_music"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  isActive={field.value}
                  onChangeIsActive={(value) => field.onChange(value)}
                  labelActive={TIKTOK_AUTO_ADD_MUSIC_LABEL}
                  labelDeactivate={TIKTOK_AUTO_ADD_MUSIC_LABEL}
                />
              )}
            />
          </Grid>
        )}
        {!isImage && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name="platformsData.tiktok.disable_duet"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    isActive={field.value}
                    onChangeIsActive={(value) => field.onChange(value)}
                    labelActive={TIKTOK_DISABLE_DUET_LABEL}
                    labelDeactivate={TIKTOK_DISABLE_DUET_LABEL}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="platformsData.tiktok.disable_stitch"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    isActive={field.value}
                    onChangeIsActive={(value) => field.onChange(value)}
                    labelActive={TIKTOK_DISABLE_STITCH_LABEL}
                    labelDeactivate={TIKTOK_DISABLE_STITCH_LABEL}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="platformsData.tiktok.is_aigc"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    isActive={field.value}
                    onChangeIsActive={(value) => field.onChange(value)}
                    labelActive={TIKTOK_IS_AIGC_LABEL}
                    labelDeactivate={TIKTOK_IS_AIGC_LABEL}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default TikTokPlatformFields;
